import { FormEvent, useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useUserHook } from "../../hooks/UserHook";
import { userService } from "../../dataservices/UserService";
import { ApiAccount } from "../../apis/ApiAccount";
import { isChangelogDialogOpen, subscribeChangelogDialog } from "./ChangelogDialog";

// Masterplan Paket 5: Wer (noch) keinen Anzeigenamen hat, wird einmal gefragt, wie wir ihn nennen
// duerfen. Reihenfolge beim ersten Login: Beta-/Changelog-Dialog -> DIESER Dialog -> Willkommens-Tour
// (TourHost wartet ueber isDisplayNameDialogOpen). "Spaeter" fragt erst in der naechsten Sitzung wieder.
const DISMISSED_KEY = 'sengi_displayname_later';

let open = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(listener => listener());
export const subscribeDisplayNameDialog = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};
export const isDisplayNameDialogOpen = () => open;
const setOpen = (value: boolean) => {
    if (open === value) return;
    open = value;
    emit();
};

const isDismissedThisSession = () => {
    try {
        return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
};

// Inline-Bearbeitung in den Einstellungen (Profil-Tab) - gleicher Endpoint wie das Popup.
export const DisplayNameEditor = ({ current }: { current: string | null | undefined }) => {
    const { t } = useTranslation('common');
    const [name, setName] = useState(current ?? '');
    const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    useEffect(() => { setName(current ?? ''); }, [current]);

    const save = async (e: FormEvent) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (trimmed.length < 1 || trimmed.length > 50) { setState('error'); return; }
        setState('saving');
        const result = await ApiAccount.UpdateProfile({ displayName: trimmed });
        if (!result.ok) { setState('error'); return; }
        await userService.forceFetchUser();
        setState('saved');
    };

    const unchanged = name.trim() === (current ?? '');
    return (
        <form className="d-flex flex-wrap gap-2 align-items-center" onSubmit={save}>
            <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 260 }}
                value={name}
                maxLength={50}
                aria-label={t('displayName.label')}
                placeholder={t('displayName.placeholder')}
                onChange={e => { setName(e.target.value); setState('idle'); }}
            />
            <button type="submit" className="btn btn-sm btn-outline-primary" disabled={state === 'saving' || unchanged || !name.trim()}>
                {state === 'saving' ? t('displayName.saving') : t('displayName.save')}
            </button>
            {state === 'saved' && <span className="small text-success">{t('displayName.saved')}</span>}
            {state === 'error' && <span className="small text-danger">{t('displayName.invalid')}</span>}
        </form>
    );
};

// In PageMasterTemplate gemountet.
export const DisplayNameDialogHost = () => {
    const { t } = useTranslation('common');
    const [user] = useUserHook();
    const changelogOpen = useSyncExternalStore(subscribeChangelogDialog, isChangelogDialogOpen);
    const isOpen = useSyncExternalStore(subscribeDisplayNameDialog, isDisplayNameDialogOpen);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const needsName = !!user && !user.user.rawDisplayName;

    useEffect(() => {
        if (needsName && !changelogOpen && !isDismissedThisSession()) setOpen(true);
        if (!needsName) setOpen(false);
    }, [needsName, changelogOpen]);

    if (!isOpen || !user) return null;

    const later = () => {
        try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch { /* egal */ }
        setOpen(false);
    };

    const save = async (e: FormEvent) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (trimmed.length < 1 || trimmed.length > 50) {
            setError(t('displayName.invalid'));
            return;
        }
        setSaving(true);
        setError(null);
        const result = await ApiAccount.UpdateProfile({ displayName: trimmed });
        setSaving(false);
        if (!result.ok) {
            setError(result.status === 400 ? t('displayName.invalid') : t('displayName.saveFailed'));
            return;
        }
        // Neu laden: aktualisiert Topbar/Settings sofort und stoesst beim PortalBackend den Abgleich
        // seiner lokalen Namens-Kopie an (getmyself, ../SengiLabsPortalBackend/TASKS.md).
        await userService.forceFetchUser();
        setOpen(false);
    };

    return createPortal(
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 999999 }}
        >
            <form
                className="sengi-dialog p-4 text-body"
                style={{ maxWidth: 420, width: '90%' }}
                onSubmit={save}
                aria-labelledby="display-name-title"
            >
                <h5 id="display-name-title">{t('displayName.title')}</h5>
                <p className="text-secondary">{t('displayName.body')}</p>
                <input
                    type="text"
                    className="form-control mb-2"
                    value={name}
                    maxLength={50}
                    autoFocus
                    placeholder={user.user.userName || t('displayName.placeholder')}
                    aria-label={t('displayName.label')}
                    onChange={e => setName(e.target.value)}
                />
                {error && <div className="small text-danger mb-2">{error}</div>}
                <div className="d-flex gap-2 justify-content-end mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={later} disabled={saving}>
                        {t('displayName.later')}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
                        {saving ? t('displayName.saving') : t('displayName.save')}
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
};
